import { Link } from 'react-router-dom';
import type { DefaultTheme } from 'styled-components/macro';
import styled, { css } from 'styled-components/macro';

import Button from 'components/Button/Button';
import ButtonIcon from 'components/ButtonIcon/ButtonIcon';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Typography from 'components/Typography/Typography';
import type { TicketsReportCardI } from 'declarations/interfaces/reports';

type GradeStylesT = {
  grade: TicketsReportCardI['grade'];
};

type StatisticSignStyledT = {
  isPositive: boolean;
};

type InvoicePercentageStyledT = {
  isPositive: boolean;
};

type ReportStateStyledT = {
  isReportOpened: boolean;
};

type CardWrapStyledT = {
  isActive: boolean;
};

const reportHeaderHeight = 48;
const cardsBlockHeight = 185;
const cardsClosedTranslateValue = -(reportHeaderHeight + cardsBlockHeight);

const A = css`
  color: ${({ theme }): string => theme.colors.primary.variant11};
`;
const B = css`
  color: ${({ theme }): string => theme.colors.primary.variant8};
`;
const C = css`
  color: ${({ theme }): string => theme.colors.primary.variant7};
`;
const D = css`
  color: ${({ theme }): string => theme.colors.highlight.variant2};
`;
const F = css`
  color: ${({ theme }): string => theme.colors.highlight.variant2};
`;

const gradeColor = {
  A,
  B,
  C,
  D,
  F,
};

export const DealerReportWrapStyled = styled.div`
  position: relative;
  width: 100%;
`;

export const CardsWrapStyled = styled(FlexContainer)<ReportStateStyledT>`
  flex-wrap: nowrap;
  gap: 4px;
  width: 100%;
  max-height: ${({ isReportOpened }): string => (isReportOpened ? 'none' : '0')};
  margin-top: 0;
  overflow: ${({ isReportOpened }): string => (isReportOpened ? 'visible' : 'hidden')};
  transform: ${({ isReportOpened }): string =>
    isReportOpened ? 'translateY(0)' : `translateY(${cardsClosedTranslateValue}px)`};
  z-index: 0;
  transition: max-height 0.25s linear, transform 0.3s linear;
`;

export const CardWrapStyled = styled(FlexContainer)<CardWrapStyledT>`
  width: fit-content;
  padding: 4px 6px;
  position: relative;
  border-radius: ${({ theme }): string => theme.radius.lg};
  background: ${({ isActive, theme }): string =>
    isActive ? theme.colors.primary.variant5 : theme.colors.surface};
  border: 1px solid ${({ theme }): string => theme.colors.primary.variant6};
`;

export const InnerContentStyled = styled(FlexContainer)`
  align-self: baseline;
  gap: 4px;
  width: fit-content;
  margin-bottom: 2px;
`;

export const ClaimReportInnerContentStyled = styled(InnerContentStyled)`
  gap: 2px;
  width: fit-content;
`;

export const CategoryLabelStyled = styled(Typography)`
  margin-top: 2px;
  font-size: 0.5rem;
  line-height: 1.2;
  color: ${({ theme }): string => theme.colors.primary.variant9};
`;

export const MessageStyled = styled(Typography)`
  margin-top: 2px;
  font-weight: ${({ theme }): string => theme.fontWeight.bold.toString()};
`;

export const GradesWrapStyled = styled.div`
  flex-shrink: 0;
`;

export const GradeLabelStyled = styled(Typography)`
  font-size: 0.5rem;
  font-weight: ${({ theme }): string => theme.fontWeight.semibold.toString()};
  color: ${({ theme }): string => theme.colors.black};
  line-height: 1.3;
  opacity: 0.3;
`;

export const CardLabelRowStyled = styled.div`
  min-height: 20px;
  display: flex;
  align-items: flex-end;
  margin-bottom: 4px;
`;

export const ClaimLabelStyled = styled(Typography)`
  margin: 0;
`;

/** Dealer Billing card column widths (5% wider than previous). */
const rightColumnWidth = 116;
const leftColumnMinWidth = 100;
const middleColumnWidth = 92;
const rowGapPx = 9;
const dealerReportGridMinWidth =
  leftColumnMinWidth + rowGapPx + middleColumnWidth + rowGapPx + rightColumnWidth;

/** Grid content: fills card; space-evenly distributes rows evenly within the card height. */
export const DealerReportCardGridStyled = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  flex: 1;
  min-height: 0;
  width: 100%;
  min-width: ${dealerReportGridMinWidth}px;

  /* Tablet and mobile: allow shrink to fit viewport */
  @media screen and (max-width: 768px) {
    min-width: 0;
  }
  @media screen and (max-width: 424px) {
    min-width: 0;
  }
`;

export const DealerReportDateLabelStyled = styled(Typography)`
  font-size: ${({ theme }): string => theme.fontSize.xs};
  color: ${({ theme }): string => theme.colors.primary.variant9};
  margin-top: 8px;
  margin-bottom: 0;
`;

/** Wraps date row cells so label + value are vertically aligned (same structure as metric rows). */
export const DealerReportDateCellStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  min-width: 0;
`;

/** Right column date cell: Current Billing Period + value; left-aligned in fixed-width column. */
export const DealerReportRightDateCellStyled = styled(DealerReportDateCellStyled)`
  width: 100%;
`;

/** Right column metric cell: Amount, Claims, Avg. Claim Amount; same layout as date cell for vertical alignment. */
export const DealerReportRightMetricCellStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
  min-width: 0;
  width: 100%;
`;

export const DealerReportMetricRowStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const DealerReportMetricLabelStyled = styled(Typography)`
  font-size: ${({ theme }): string => theme.fontSize.xs};
  color: ${({ theme }): string => theme.colors.primary.variant9};
`;

export const DealerReportMetricValueStyled = styled(Typography)`
  font-weight: ${({ theme }): string => theme.fontWeight.semibold.toString()};
  color: ${({ theme }): string => theme.colors.primary.variant11};
`;

/** One row: left (flex, has min so it never collapses) | middle % | right. Row min-width keeps layout horizontal. */
export const DealerReportRowStyled = styled.div`
  display: grid;
  grid-template-columns: minmax(${leftColumnMinWidth}px, 1fr) ${middleColumnWidth}px ${rightColumnWidth}px;
  gap: ${rowGapPx}px;
  align-items: end;
  min-width: ${dealerReportGridMinWidth}px;

  @media screen and (max-width: 768px) {
    min-width: 0;
    grid-template-columns: 1fr minmax(${middleColumnWidth}px, auto) minmax(
        ${rightColumnWidth}px,
        auto
      );
  }
  @media screen and (max-width: 424px) {
    min-width: 0;
    grid-template-columns: 1fr minmax(${middleColumnWidth}px, auto) minmax(
        ${rightColumnWidth}px,
        auto
      );
  }
`;

/** Middle cell in a row: shows change %. Centered in its column. */
export const DealerReportChangeCellStyled = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding-bottom: 3px;
`;

/** Percentage change text: same size for all rows. zero=black, increase=green, decrease=red. $negativeIsGood inverts colors. */
type ChangePercentTextStyledT = {
  $variant: 'zero' | 'increase' | 'decrease';
  $negativeIsGood?: boolean;
};
const getChangePercentColor = (
  theme: DefaultTheme,
  variant: ChangePercentTextStyledT['$variant'],
  negativeIsGood?: boolean
): string => {
  if (variant === 'zero') return negativeIsGood ? theme.colors.white : theme.colors.black;
  const green = theme.colors.secondary.variant2;
  const red = theme.colors.highlight.variant2;
  if (negativeIsGood) {
    return variant === 'decrease' ? green : red;
  }
  return variant === 'increase' ? green : red;
};
export const ChangePercentTextStyled = styled.span<ChangePercentTextStyledT>`
  font-size: ${({ theme }): string => theme.fontSize.sm};
  font-weight: ${({ theme }): string => theme.fontWeight.medium.toString()};
  color: ${({ theme, $variant, $negativeIsGood }): string =>
    getChangePercentColor(theme, $variant, $negativeIsGood)};
`;

/** Triangle: points up for increase, down for decrease. Same size for all rows. $negativeIsGood makes decrease green. */
type ChangeTriangleStyledT = { $isIncrease: boolean; $negativeIsGood?: boolean };
const getTriangleStyles = (
  isIncrease: boolean,
  negativeIsGood: boolean | undefined,
  theme: DefaultTheme
): { bottom: string; top: string } => {
  const green = theme.colors.secondary.variant2;
  const red = theme.colors.highlight.variant2;
  const isGood = negativeIsGood ? !isIncrease : isIncrease;
  const color = isGood ? green : red;
  return {
    bottom: isIncrease ? `6px solid ${color}` : 'none',
    top: isIncrease ? 'none' : `6px solid ${color}`,
  };
};
export const ChangeTriangleStyled = styled.span<ChangeTriangleStyledT>`
  display: inline-block;
  width: 0;
  height: 0;
  margin: 0 4px 0 0;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: ${({ $isIncrease, $negativeIsGood, theme }): string =>
    getTriangleStyles($isIncrease, $negativeIsGood, theme).bottom};
  border-top: ${({ $isIncrease, $negativeIsGood, theme }): string =>
    getTriangleStyles($isIncrease, $negativeIsGood, theme).top};
`;

type DealerReportChangeStyledT = { $isPositive: boolean };
export const DealerReportChangeStyled = styled(Typography)<DealerReportChangeStyledT>`
  font-size: ${({ theme }): string => theme.fontSize.sm};
  font-weight: ${({ theme }): string => theme.fontWeight.medium.toString()};
  color: ${({ theme, $isPositive }): string =>
    $isPositive ? theme.colors.primary.variant11 : theme.colors.highlight.variant2};
`;

export const PointStyled = styled(Typography)`
  font-weight: ${({ theme }): string => theme.fontWeight.extraBold.toString()};
  font-size: ${({ theme }): string => theme.fontSize['2xl']};
  color: ${({ theme }): string => theme.colors.primary.variant11};
`;

export const GradeItemStyled = styled.div`
  margin-top: 16px;
  &:first-child {
    margin-top: 0;
  }
`;

export const FooterWrapStyled = styled(FlexContainer)`
  width: 100%;
  height: 100%;
  min-height: 38px;
  background: ${({ theme }): string => theme.colors.primary.variant3};
  padding: 7px 9px;
  border-radius: ${({ theme }): string => theme.radius.lg};
`;

export const FooterStyled = styled(FlexContainer)`
  gap: 6px;
  width: 100%;
`;

export const FooterContentStyled = styled(Typography)`
  font-weight: ${({ theme }): string => theme.fontWeight.bold.toString()};
  font-size: 0.625rem;
  line-height: 1.2;
  color: ${({ theme }): string => theme.colors.primary.variant9};
  flex: 1;
`;

export const GradeStyled = styled(Typography)<GradeStylesT>`
  margin-right: 10px;
  font-size: 2.3rem;
  font-weight: ${({ theme }): string => theme.fontWeight.black.toString()};
  ${({ grade }): ReturnType<typeof css> => gradeColor[grade]}
`;

export const HeaderStyled = styled(FlexContainer)<ReportStateStyledT>`
  position: relative;
  flex-direction: column-reverse;
  gap: 0.5rem;
  align-items: end;
  margin-bottom: ${({ isReportOpened }): string => (isReportOpened ? '0' : '10px')};
  padding: 1rem;
  background-color: ${({ theme }): string => theme.colors.surface};
  transition: margin-bottom 0.3s linear;
  z-index: 1;

  ${({ theme }): string => `
    ${theme.devices.tablet} {
      flex-direction: row;
      gap: 2rem;
      align-items: center;
    }
  `}
`;

export const FlexContainerStyled = styled(FlexContainer)`
  gap: 0.75rem;
  margin-left: auto;
`;

export const ButtonCollapseStyled = styled(ButtonIcon)<ReportStateStyledT>`
  height: 26px;

  svg {
    transform: rotate(${({ isReportOpened }): string => (isReportOpened ? '180deg' : '0')});
    transition: 0.3s;
  }

  &:hover {
    svg {
      transform: rotate(${({ isReportOpened }): string => (isReportOpened ? '0' : '180deg')});
    }
  }
`;

export const HelpBtnIconStyled = styled(ButtonIcon)`
  padding: 0;
`;

export const getPopupContentStyle = (
  theme: DefaultTheme,
  isMobile: boolean
): Record<string, string> => ({
  minWidth: isMobile ? '180px' : '145px',
  borderRadius: theme.radius.sm,
});

export const MenuItemStyled = styled.li`
  &:hover {
    background-color: ${({ theme }): string => theme.colors.surface};
  }

  &:only-of-type {
    border-radius: ${({ theme }): string => `${theme.radius.sm}`};
  }

  &:not(:only-of-type) {
    &:first-of-type {
      border-radius: ${({ theme }): string => `${theme.radius.sm} ${theme.radius.sm} 0 0`};
    }

    &:last-of-type {
      border-radius: ${({ theme }): string => `0 0 ${theme.radius.sm} ${theme.radius.sm}`};
    }
  }
`;

const baseNavControl = css`
  width: 100%;
  height: auto;
  justify-content: flex-start;
  padding: 1rem 0.875rem;
  font-weight: ${({ theme }): string => theme.fontWeight.medium.toString()};
  font-size: ${({ theme }): string => theme.fontSize['2xl']};
`;

export const MenuBtnStyled = styled(Button)`
  ${baseNavControl}

  ${({ theme }): string => `
    ${theme.devices.tablet} {
      font-size: ${theme.fontSize.md};
      padding: 0.875rem;
    }
  `}
`;

export const LinkStyled = styled(Link)`
  ${baseNavControl}

  display: inline-flex;
  color: ${({ theme }): string => theme.colors.primary.variant1};

  ${({ theme }): string => `
    ${theme.devices.tablet} {
      font-size: ${theme.fontSize.md};
      padding: 0.875rem;
    }
  `}
`;

export const ReportsLabelStyled = styled(FlexContainer)<ReportStateStyledT>`
  position: relative;
  max-height: ${({ isReportOpened }): string => (isReportOpened ? '20px' : '0')};
  padding: 4px 0px 6px 3px;
  transform: ${({ isReportOpened }): string =>
    isReportOpened ? 'translateY(10px)' : `translateY(${cardsClosedTranslateValue}px)`};
  transition: max-height 0.25s linear, transform 0.3s linear;
`;

export const InnerContentRowStyled = styled(FlexContainer)`
  width: fit-content;
  gap: 12px;
  justify-content: flex-start !important;
  align-items: stretch;
  flex-wrap: nowrap;

  > div {
    margin: 0;
    padding: 0;
    flex-shrink: 0;
    white-space: nowrap;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const StatisticSignStyled = styled.div<StatisticSignStyledT>`
  width: 0;
  height: 0;
  margin: 0 4px 0 6px;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-bottom: ${({ isPositive, theme }): string =>
    isPositive ? `6px solid ${theme.colors.secondary.variant2}` : 'none'};
  border-top: ${({ isPositive, theme }): string =>
    isPositive ? 'none' : `6px solid ${theme.colors.highlight.variant5}`};
`;

export const InvoicePercentageStyled = styled(Typography)<InvoicePercentageStyledT>`
  color: ${({ isPositive, theme }): string =>
    isPositive ? theme.colors.secondary.variant1 : theme.colors.highlight.variant2};
`;

export const StatisticSignLargeStyled = styled(StatisticSignStyled)`
  margin: 0 2px;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-bottom: ${({ isPositive, theme }): string =>
    isPositive ? `8px solid ${theme.colors.secondary.variant2}` : 'none'};
  border-top: ${({ isPositive, theme }): string =>
    isPositive ? 'none' : `8px solid ${theme.colors.highlight.variant5}`};
`;

export const InnerFlexContainerStyled = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  justify-content: flex-end;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
  ${({ theme }): string => `
    ${theme.devices.largeDesktop} {
      width: auto;
    }
  `}
`;
